import Reveal from './Reveal'
import KineticText from './KineticText'
import useParallax from '../../hooks/useParallax'

/* ── Section shell ───────────────────────────────────────────────────────
   Sections are separated structurally: a hairline between them, and an
   alternate tonal ground (`tone="raised"`) so the page reads as a sequence of
   plates. An optional `atmosphere` adds one pool of light behind the content
   that drifts at its own depth on scroll — space, without scenery. */
const TONES = {
  base: '',
  raised: 'plate',
}
const ATMOSPHERES = {
  right: 'wash wash-primary right-[-10%] top-[3rem] h-[30rem] w-[42rem]',
  left: 'wash wash-primary left-[-12%] top-[4rem] h-[28rem] w-[40rem]',
}

export function Section({ id, className = '', containerClassName = '', tone = 'base', atmosphere, children }) {
  const parallax = useParallax(atmosphere ? 56 : 0)
  return (
    <section
      ref={atmosphere ? parallax : undefined}
      id={id}
      className={`scroll-mt-24 border-b border-outline-variant py-20 md:py-28 2xl:py-32 ${atmosphere ? 'relative' : ''} ${TONES[tone] || ''} ${className}`}
    >
      {atmosphere && (
        <div className="section-atmos" aria-hidden="true">
          <div className="depth-layer" style={{ '--depth': 1 }}>
            <div className={ATMOSPHERES[atmosphere]} />
          </div>
        </div>
      )}
      <div className={`mx-auto max-w-6xl xl:max-w-7xl 2xl:max-w-[90rem] px-6 lg:px-10 2xl:px-16 ${atmosphere ? 'relative z-[1]' : ''} ${containerClassName}`}>
        {children}
      </div>
    </section>
  )
}

// Section header: a labelled eyebrow, the headline, and an optional lead.
export function SectionHeading({ eyebrow, title, lead, align = 'left', className = '' }) {
  const alignCls = align === 'center' ? 'text-center mx-auto' : 'text-left'
  return (
    <div className={`max-w-2xl xl:max-w-3xl ${alignCls} ${className}`}>
      {eyebrow && (
        <Reveal as="p" y={8} className="eyebrow mb-5">
          {eyebrow}
        </Reveal>
      )}
      <KineticText
        as="h2"
        text={title}
        delay={0.05}
        each={0.03}
        className="text-[2.1rem] sm:text-4xl md:text-[2.9rem] 2xl:text-[3.4rem] font-bold tracking-[-0.03em] text-ink text-balance leading-[1.05]"
      />
      {lead && (
        <Reveal as="p" delay={0.15} y={10} className="mt-5 text-lg 2xl:text-xl text-ink-3 leading-relaxed text-pretty">
          {lead}
        </Reveal>
      )}
    </div>
  )
}
